import type { CollectionItem, Collection } from '../types/collections';

/**
 * Whitelist of user-facing fields per system collection. System collections
 * (e.g. Places) carry internal columns like `google_place_id` and `source`
 * that aren't meant for direct editing or display. The detail page and the
 * create/edit modals filter the schema's field list through this map before
 * rendering.
 *
 * Non-system collections fall through and show all fields.
 */
const SYSTEM_COLLECTION_VISIBLE_FIELDS: Record<string, string[]> = {
  places: ['name', 'address', 'lat', 'lng'],
};

/**
 * Defaults to auto-fill for hidden fields when creating an item in a system
 * collection. The schema may declare these required (e.g. Places.source) but
 * we don't surface them in the form, so the create path supplies a value
 * server-side-equivalent here. Edits don't apply these — they preserve
 * whatever the existing item has.
 */
const SYSTEM_COLLECTION_HIDDEN_DEFAULTS: Record<string, Record<string, any>> = {
  places: { source: 'user' },
};

/**
 * Return the field names to surface in the UI for a given collection,
 * sorted by their configured `position`. For system collections, the result
 * is the intersection of the whitelist and the actual schema (so removed
 * fields don't render). For everything else, all fields.
 */
/**
 * What one row of this collection is called in UI copy ("person" for the
 * People system collection, "place" for Places, generic "item" otherwise).
 * Lowercase — capitalize at the call site for Title Case labels.
 */
export function collectionItemNoun(
  collection: Pick<Collection, 'system_kind'> | null | undefined,
): 'person' | 'place' | 'item' {
  if (collection?.system_kind === 'people') return 'person';
  if (collection?.system_kind === 'places') return 'place';
  return 'item';
}

export function getVisibleFieldNames(collection: Collection): string[] {
  const all = Object.entries(collection.config?.fields ?? {})
    .sort(([, a], [, b]) => (a.position ?? 0) - (b.position ?? 0))
    .map(([name]) => name);

  if (!collection.is_system || !collection.system_kind) return all;

  const whitelist = SYSTEM_COLLECTION_VISIBLE_FIELDS[collection.system_kind];
  if (!whitelist) return all;

  return all.filter((name) => whitelist.includes(name));
}

/**
 * Defaults to merge into the create payload for a system collection so
 * hidden required fields are populated even though they're not in the form.
 * Returns an empty object for non-system collections.
 */
export function getHiddenFieldDefaults(collection: Collection): Record<string, any> {
  if (!collection.is_system || !collection.system_kind) return {};
  return SYSTEM_COLLECTION_HIDDEN_DEFAULTS[collection.system_kind] ?? {};
}

/**
 * Resolve the user-facing label for a collection item id from a
 * `collectionItems` map. Prefers the embedded collection's `display_template`
 * so collections whose label field isn't literally `name` (e.g. People →
 * `{first name}`) render canonically. Falls back to `first name`/`name`
 * for items that arrive without embedded collection metadata, and returns
 * `null` when the item isn't in the cache at all — callers layer their own
 * `#<id>` / "name hint" fallbacks on top.
 */
export function resolveCollectionItemName(
  itemId: number | string,
  collectionItems: Record<string, any>,
): string | null {
  const item = collectionItems[String(itemId)];
  if (!item) return null;
  if (item.collection) {
    const rendered = renderCollectionItem(item, item.collection);
    if (rendered) return rendered;
  }
  return item.data?.['first name'] || item.data?.name || null;
}

/**
 * Renders a collection item using the collection's display template.
 *
 * @param item - The collection item to render
 * @param collection - The collection definition containing the display template
 * @returns The rendered string representation of the item
 */
export function renderCollectionItem(item: CollectionItem, collection: Collection): string {
  const { display_template, fields } = collection.config;
  
  // Replace template placeholders with actual values
  return display_template.replace(/\{([^}]+)\}/g, (_match, fieldName) => {
    const value = item.data[fieldName];
    
    // Handle null/undefined values
    if (value === null || value === undefined) {
      return '';
    }
    
    // Convert to string based on field type
    const field = fields[fieldName];
    if (!field) {
      return String(value);
    }
    
    switch (field.type) {
      case 'boolean':
        return value ? 'Yes' : 'No';
      case 'picture':
        return '[Image]';
      default:
        return String(value);
    }
  }).trim();
}

/**
 * Validates a display template to ensure all referenced fields exist
 * @param template - The display template string
 * @param fields - The available fields
 * @returns An error message if invalid, or null if valid
 */
export function validateDisplayTemplate(
  template: string, 
  fields: Record<string, any>
): string | null {
  const matches = template.match(/\{([^}]+)\}/g);
  if (!matches) {
    return null;
  }
  
  const fieldNames = Object.keys(fields);
  const invalidFields: string[] = [];
  
  for (const match of matches) {
    const fieldName = match.slice(1, -1); // Remove { and }
    if (!fieldNames.includes(fieldName)) {
      invalidFields.push(fieldName);
    }
  }
  
  if (invalidFields.length > 0) {
    return `Invalid field names in template: ${invalidFields.join(', ')}`;
  }
  
  return null;
}